// get current env
import {AutocompleteValue, ErrorObject, ItemCost} from '../types';
import theme from '../theme';
import {Dispatch} from 'react';
import {IntlShape} from 'react-intl';
import {LanguageEnum, NormalizeLanguageEnum} from 'src/enums';

export const isDevelopmentMode = () => process.env.NODE_ENV === 'development';

// get current BASE_URL
export const getBaseURL = () => process.env.REACT_APP_BACKEND_URL;

// get current WS_BASE_URL
export const getWSBaseURL = () => process.env.REACT_APP_WS_URL;

// get central-service OCPP websocket base — the address a charging station dials, shown to the
// technician who configures it. Per environment, because a station set up from the dev dashboard
// against the prod address would report real sessions into prod.
export const getOcppWsURL = () => process.env.REACT_APP_OCPP_WS_URL;

// get report-service BASE_URL
export const getReportServiceURL = () => process.env.REACT_APP_REPORT_SERVICE_URL;

// get ai-agent-service BASE_URL
export const getAiAgentServiceURL = () => process.env.REACT_APP_AI_AGENT_SERVICE_URL;

export const getMapBaseAPI = () => process.env.REACT_APP_GOOGLE_API_KEY;

export const convertTime = (value: number) => {
    if (!value) {
        return 0;
    }
    const minutes = value % 60;
    const hours = Math.floor(value / 60);

    return `${formatTime(hours)}h:${formatTime(minutes)}m`;
};

export const formatTime = (time: number | string) => {
    return String(time).length === 1 ? `0${time}` : time;
};

export enum AppRouteEnum {
    app = 'app',
    locations = 'locations',
    chargers = 'chargers',
    chargingSessions = 'charging-sessions',
    business = 'business',
    businesses = 'businesses',
    users = 'users',
    contracts = 'contracts',
    tariffs = 'tariffs',
    invoices = 'invoices',
    signUp = 'signUp',
    login = 'login',
    welcome = 'welcome',
    forgotPassword = 'forgot',
    completeRegistration = 'completeRegistration',
    confirmRegistration = 'confirmRegistration',
    resetPassword = 'resetPassword',
    verifyEmail = 'verifyEmail',
    corporateUserWelcome = 'corporateUserWelcome',
    configurations = 'configurations',
    logs = 'logs',
    reports = 'reports',
    assistant = 'assistant',
    termsOfUse = 'terms-of-use',
    termsOfUseEn = 'terms-of-use/en',
    termsOfUseFr = 'terms-of-use/fr',
    termsOfUseDe = 'terms-of-use/de',
    privacyPolicy = 'privacy-policy',
    privacyPolicyEn = 'privacy-policy/en',
    privacyPolicyFr = 'privacy-policy/fr',
    privacyPolicyDe = 'privacy-policy/de',
    support = 'support',
    supportEn = 'support/en',
    supportFr = 'support/fr',
    supportDe = 'support/de',
    notifications = 'notifications',
    technicalTeam = 'technical-team',
    accessControl = 'access-control',
    roles = 'roles',
    permissions = 'permissions',
    reOwner = 're-owners',
    rfid = 'rfids',
    employee = 'employees',
    more = 'more',
}

export const AppRouteSpace = (parts: (AppRouteEnum | string)[], absolute = true) => {
    const path = parts.join('/');
    return absolute ? `/${path}` : path;
};

export const getObjectFieldAsString = <T = unknown>(obj: T, field: string) => {
    if (field in obj) {
        return '' + obj[field as keyof T];
    }
    return undefined;
};

export const convertMinutes = (num: number) => {
    const hours = num / 60;
    const rhours = Math.floor(hours);
    const minutes = (hours - rhours) * 60;
    const rminutes = Math.round(minutes);
    return `${rhours}h ${rminutes}m`;
};

export const findAutocompleteValueById = (options: AutocompleteValue[], value?: number) => {
    if (!value) {
        return undefined;
    }
    return options.find((option) => option.id == value);
};

export const findAutocompleteValueByLabel = (options: AutocompleteValue[], value?: string) => {
    if (!value) {
        return undefined;
    }
    return options.find((option) => option.label === value);
};

export const isErrorObject = (input: unknown): input is ErrorObject => {
    if (!(input instanceof Object) || !Object.keys(input).includes('data')) {
        return false;
    }
    // The key alone is not enough: `data` may be undefined (a rejection that
    // carried no response body) or a bare marker string (an aborted request).
    // Callers read `data.message` right away, so anything but an object must
    // not pass the guard.
    return (input as {data: unknown}).data instanceof Object;
};
export const getNumberFromPx = (num: number) => +theme.spacing(num / 8).replace('px', '');

export const formatFileName = (name: string) =>
    name.replace(/^(\d+)(-)(.+)$/, '$3').replace(/_/g, ' ');

export const valuesTrimmer = <T = unknown>(obj: T) => {
    const objMap = new Map<string, unknown>();
    for (const key in obj) {
        const item = obj[key];
        if (typeof item === 'string') {
            objMap.set(key, item.trim());

        }
    }
    return {
        ...obj,
        ...Object.fromEntries(objMap),
    } as T;
};

/**
 * A live collection holding the newest word on each subject.
 *
 * `identify` says what a subject is, and that is the whole decision. Keyed on the charger, a station
 * whose two sockets were both charging kept only whichever update arrived last: the two sessions took
 * turns in one slot, so the figures on screen belonged to one connector and then to the other, with
 * nothing saying which. News about the charger itself is one row per charger; anything a connector
 * reports is one row per connector.
 */
export const destinctSliceCollection = <T>(
    collection: T[],
    payload: T,
    identify: (item: T) => string
) => {
    const subject = identify(payload);
    return collection.filter((item) => identify(item) !== subject).concat([payload]);
};

export const handleScroll = (
    currentPage: number,
    totalPages: number,
    page: number,
    setPage: Dispatch<number>
) => {
    if (currentPage < totalPages - 1) {
        setPage(page + 1);
    }
};

export const generateItemCost = (costs: ItemCost[]) => {
    const initTotal = costs.reduce((sum, cost) => sum + cost.initCost, 0);
    const amortizedTotal = costs.reduce((sum, cost) => sum + cost.currentCost, 0);
    const currency = costs[0]?.currency ?? '';
    return {initTotal, amortizedTotal, currency};
};

export const processedLanguages = (intl: IntlShape) => [
    {
        id: 0,
        name: intl.formatMessage({id: NormalizeLanguageEnum.ENGLISH}),
    value: LanguageEnum.ENGLISH,
    },
    {
        id: 1,
        name: intl.formatMessage({id: NormalizeLanguageEnum.FRENCH}),
        value: LanguageEnum.FRENCH,
    },
    {
        id: 2,
        name: intl.formatMessage({id: 'german'}),
        value: LanguageEnum.DEUTSCH,
    },
    {
        id: 3,
        name: intl.formatMessage({id: 'Ukrainian'}),
        value: LanguageEnum.UKRAINIAN,
    },
];

/**
 * Format price by removing trailing zeros
 * @param price - Price in the smallest currency unit (e.g., 10000 = 1.0000)
 * @returns Formatted price string without trailing zeros
 * @example
 * formatPrice(3000) => "0.3"
 * formatPrice(3400) => "0.34"
 * formatPrice(125780) => "12.578"
 * formatPrice(10000) => "1"
 */
export const formatPrice = (price: number): string => {
    const priceInUnits = price / 10000;
    // Convert to string with 4 decimal places, then remove trailing zeros and decimal point if not needed
    return priceInUnits.toFixed(4).replace(/\.?0+$/, '');
};
